import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert
} from '@mui/material';
import { Add, Edit, Delete, Security, People } from '@mui/icons-material';
import { roleAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const { canCreateModule, canUpdateModule, canDeleteModule } = useAuth();
  const canCreateRole = canCreateModule('role_management');
  const canUpdateRole = canUpdateModule('role_management');
  const canDeleteRole = canDeleteModule('role_management');

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await roleAPI.getRoles();
      setRoles(response.data);
      console.log("Fetched roles:", response.data, response.data.map(role => role.name));
    } catch (error) {
      console.error('Error fetching roles:', error);
      showAlert('Error fetching roles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: '', severity: 'success' }), 5000);
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        description: role.description
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        name: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await roleAPI.updateRole(editingRole.id, roleForm);
        showAlert('Role updated successfully');
      } else {
        await roleAPI.createRole(roleForm);
        showAlert('Role created successfully');
      }
      fetchRoles();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving role:', error);
      showAlert('Error saving role', 'error');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      try {
        await roleAPI.deleteRole(roleId);
        showAlert('Role deleted successfully');
        fetchRoles();
      } catch (error) {
        console.error('Error deleting role:', error);
        showAlert('Error deleting role', 'error');
      }
    }
  };

  const getRoleColor = (roleName) => {
    const colors = {
      'SUPERADMIN': 'error',
      'SALES': 'primary',
      'ACCOUNTS': 'success',
      'PURCHASE': 'warning',
      'default': 'default'
    };
    return colors[roleName] || colors.default;
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Typography>Loading roles...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {alert.open && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Role Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage user roles and access permissions
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    Total Roles
                  </Typography>
                  <Typography variant="h4" component="div">
                    {roles.length}
                  </Typography>
                </Box>
                <Security sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="overline">
                    System Roles
                  </Typography>
                  <Typography variant="h4" component="div" color="info.main">
                    {roles.filter(r => ['SUPERADMIN', 'SALES', 'ACCOUNTS', 'PURCHASE'].includes(r.name)).length}
                  </Typography>
                </Box>
                <People sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <Typography variant="h6" color="textSecondary">
          All Roles ({roles.length})
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />} 
          onClick={() => handleOpenDialog()}
          disabled={!canCreateRole}
        >
          Add Role
        </Button>
      </Box>

      {/* Roles Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell><strong>Role Name</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow 
                key={role.id}
                sx={{ '&:hover': { backgroundColor: 'grey.50' } }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Security color="primary" />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {role.name}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {role.description || 'No description provided'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={['SUPERADMIN', 'SALES', 'ACCOUNTS', 'PURCHASE'].includes(role.name) ? 'System' : 'Custom'} 
                    color={['SUPERADMIN', 'SALES', 'ACCOUNTS', 'PURCHASE'].includes(role.name) ? 'primary' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(role.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(role)}
                      disabled={!canUpdateRole}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRole(role.id)}
                      disabled={!canDeleteRole || role.name === 'SUPERADMIN'}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {roles.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No roles found
          </Typography>
        </Box>
      )}

      {/* Add/Edit Role Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {editingRole ? 'Edit Role' : 'Create New Role'}
          </Typography>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <TextField
                label="Role Name"
                name="name"
                value={roleForm.name}
                onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                required
                fullWidth
                placeholder="e.g., SALES_MANAGER"
                helperText="Use uppercase with underscores for consistency"
              />
              <TextField
                label="Description"
                name="description"
                value={roleForm.description}
                onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                multiline
                rows={3}
                fullWidth
                placeholder="Describe the role's permissions and responsibilities"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" size="large">
              {editingRole ? 'Update Role' : 'Create Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default RoleManagement;